import React, { ReactNode, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from './AuthModal';
import { User } from '../../services/authService';

interface AuthGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  showAuthModal?: boolean;
  onShowAuthModal?: (show: boolean) => void;
  onAuthChange?: (user: User | null, token?: string) => void;
  loadingComponent?: ReactNode;
  title?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = true,
  showAuthModal = false,
  onShowAuthModal,
  onAuthChange,
  loadingComponent,
  title = "Welcome to LifeOS"
}): JSX.Element => {
  const { user, loading: authLoading, login } = useAuth();

  // Auto-show modal when user is not authenticated and auth is required
  useEffect(() => {
    if (requireAuth && !authLoading && !user && onShowAuthModal) {
      console.log('AuthGuard: Showing auth modal - no user detected');
      onShowAuthModal(true);
    } else if (user && onShowAuthModal) {
      console.log('AuthGuard: Hiding auth modal - user exists:', user.id);
      onShowAuthModal(false);
    }
  }, [user, authLoading, requireAuth, onShowAuthModal]);

  const handleAuthChange = (user: User | null, token?: string) => {
    console.log('AuthGuard: handleAuthChange called with:', user, token);
    if (user && token) {
      console.log('AuthGuard: Calling login with user and token');
      login(user, token);
      if (onShowAuthModal) {
        onShowAuthModal(false);
      }
    }
    // Call parent's auth change handler if provided
    if (onAuthChange) {
      onAuthChange(user, token);
    }
  };

  const handleCloseModal = () => {
    if (onShowAuthModal) {
      onShowAuthModal(false);
    }
  };

  // Show loading if auth is loading
  if (authLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    return (
      <div className="loading-container">
        <div className="loading-content">
          <h2>📚 Loading LifeOS...</h2>
          <div className="loading-spinner"></div>
          <p>Initializing your library...</p>
        </div>
      </div>
    );
  }

  // If auth is required but user is not authenticated, show placeholder
  if (requireAuth && !user) {
    return (
      <>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '1rem',
          padding: '2rem',
          textAlign: 'center',
          color: '#64748b'
        }}>
          <h2>Please sign in to access your notes</h2>
          <p>Your personal LifeOS awaits...</p>
          {onShowAuthModal && (
            <button 
              onClick={() => onShowAuthModal(true)}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              Sign In
            </button>
          )}
        </div>
        
        <AuthModal
          isOpen={showAuthModal}
          onClose={handleCloseModal}
          onAuthChange={handleAuthChange}
          title={title}
        />
      </>
    );
  }

  // Render children with auth modal
  return (
    <>
      {children}
      <AuthModal
        isOpen={showAuthModal}
        onClose={handleCloseModal}
        onAuthChange={handleAuthChange}
        title={title}
      />
    </>
  );
};

export default AuthGuard;
